import type { PaletteKind } from "./paletteRegistry";
import { markColorsPicked } from "./chartColorPick";
import { findBuiltInPalette } from "./paletteRegistry";

type SpecObject = Record<string, unknown>;

export interface ChartPaletteInput {
  kind: PaletteKind;
  colors: readonly string[];
}

const isObject = (value: unknown): value is SpecObject =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const COLOR_CHANNELS = ["color", "fill", "stroke"] as const;
const NESTED_LISTS = ["layer", "concat", "hconcat", "vconcat"] as const;

/**
 * One colour channel. A field channel gets the palette as its scale `range` (Vega-Lite
 * interpolates a range for a quantitative field, so sequential and diverging read as a
 * ramp); any `scheme` is dropped so it cannot win over the range. A diverging palette on a
 * quantitative field centres on zero unless the spec already sets `domainMid`. A static
 * `value` takes the first colour of a categorical palette and is left alone otherwise.
 */
const recolorChannel = (channel: SpecObject, palette: ChartPaletteInput): SpecObject => {
  if ("field" in channel || "aggregate" in channel) {
    const source: SpecObject = isObject(channel.scale) ? channel.scale : {};
    const { scheme: _scheme, ...scale } = source;
    const next: SpecObject = { ...scale, range: [...palette.colors] };
    if (palette.kind === "diverging" && channel.type === "quantitative" && !("domainMid" in next)) {
      next.domainMid = 0;
    }
    return { ...channel, scale: next };
  }
  if ("value" in channel && palette.kind === "categorical") return { ...channel, value: palette.colors[0] };
  return channel;
};

const recolorEncoding = (encoding: SpecObject, palette: ChartPaletteInput): SpecObject => {
  let next = encoding;
  COLOR_CHANNELS.forEach((key) => {
    const channel = encoding[key];
    if (isObject(channel)) next = { ...next, [key]: recolorChannel(channel, palette) };
  });
  return next;
};

const NON_SERIES_MARKS = new Set(["text", "rule", "tick", "image"]);

/**
 * A single-series chart has no colour channel to repaint: its one colour is the mark's own.
 * That colour takes the palette's first colour (the lead of a categorical palette, the end
 * of a ramp otherwise), so the palette is never a silent no-op. Annotation marks keep theirs.
 */
const recolorMark = (view: SpecObject, palette: ChartPaletteInput): SpecObject => {
  const mark = view.mark;
  const type = typeof mark === "string" ? mark : isObject(mark) && typeof mark.type === "string" ? mark.type : "";
  if (!type || NON_SERIES_MARKS.has(type)) return view;
  const encoding = isObject(view.encoding) ? view.encoding : {};
  if (COLOR_CHANNELS.some((key) => key in encoding)) return view;
  const color = palette.kind === "categorical" ? palette.colors[0] : palette.colors[palette.colors.length - 1];
  return { ...view, mark: { ...(isObject(mark) ? mark : {}), type, color } };
};

const recolorView = (view: SpecObject, palette: ChartPaletteInput): SpecObject => {
  let next = recolorMark(view, palette);
  if (isObject(view.encoding)) next = { ...next, encoding: recolorEncoding(view.encoding, palette) };
  NESTED_LISTS.forEach((key) => {
    const list = view[key];
    if (Array.isArray(list)) {
      next = { ...next, [key]: list.map((child) => (isObject(child) ? recolorView(child, palette) : child)) };
    }
  });
  // A facet or repeat wraps its unit spec under `spec`.
  if (isObject(view.spec)) next = { ...next, spec: recolorView(view.spec, palette) };
  return next;
};

/**
 * Paints a Vega-Lite spec with a palette: every colour channel (`color`, `fill`, `stroke`) of
 * every view (unit, layer, concat, facet) gets the palette as its scale range. Never adds a
 * colour field the chart doesn't have. Pure; returns the same spec when there are no colours.
 * Kept apart from the widget's selection/opacity encoding so the two never collide.
 */
export function applyChartPalette<T>(spec: T, palette: ChartPaletteInput | null | undefined): T {
  if (!palette || palette.colors.length === 0 || !isObject(spec)) return spec;
  return recolorView(spec, palette) as T;
}

/** The parts of a canvas item this reads: its spec and the palette its look names. */
export interface PalettedItemLike {
  spec?: unknown;
  look?: { format?: { palette?: { kind: string; value: string } | null } | null } | null;
}

const cache = new WeakMap<object, Map<string, unknown>>();

/**
 * An item's spec with its look's built-in palette painted on. Custom (tenant) palettes are
 * compiled into the spec by the server, so only built-ins are applied here. Memoised per
 * spec object, so the canvas sees the same spec object until the spec or palette changes. A chart
 * whose look names no palette takes the story default (`storyPaletteId`).
 */
export function withBuiltInPalette<T>(
  item: PalettedItemLike & { spec?: T },
  storyPaletteId?: string | null,
): T | undefined {
  const { spec } = item;
  const ref = item.look?.format?.palette;
  // The chart's own palette, then the story's default.
  const builtIn = ref
    ? ref.kind === "built_in"
      ? findBuiltInPalette(ref.value)
      : undefined
    : findBuiltInPalette(storyPaletteId);
  if (!isObject(spec)) return spec;
  // Any palette the user chose in Chart Studio (built-in, or a tenant palette the server compiled in) keeps its colours over the theme's.
  if (!builtIn) return ref ? markColorsPicked(spec as T) : spec;
  const byPalette = cache.get(spec) ?? new Map<string, unknown>();
  cache.set(spec, byPalette);
  if (!byPalette.has(builtIn.id)) byPalette.set(builtIn.id, markColorsPicked(applyChartPalette(spec, builtIn)));
  return byPalette.get(builtIn.id) as T;
}
